import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { MapPin, Search } from 'lucide-react';
import { useHomeFilters } from '../context';
import { reverseGeocode, getAddressSuggestions, debounce, AddressSuggestion } from '../../../lib/utils';
import { useToast } from '../../../components/ui/use-toast';

/**
 * CommuteFilter Component
 * 
 * This component handles commute address input and drive time filtering.
 */
export const CommuteFilter: React.FC = () => {
  const { toast } = useToast();
  const {
    commuteAddressRent,
    setCommuteAddressRent,
    commuteDriveTimeRent,
    setCommuteDriveTimeRent,
    commuteSuggestions,
    setCommuteSuggestions,
    commuteAddressTouched,
    setCommuteAddressTouched,
    setCommuteCoords,
    setDriveTimes,
    setSearchCity,
    setSearchState,
    listings,
    driveTimes,
    isSearching,
    setIsSearching,
    isCalculatingDriveTimes,
    // Using OpenStreetMap instead of Google Maps
  } = useHomeFilters();

  const autocompleteRef = useRef<HTMLInputElement>(null);
  const autocompleteDropdownRef = useRef<HTMLDivElement>(null);
  const [autocompleteActiveIndex, setAutocompleteActiveIndex] = useState(-1);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Track the current request to prevent race conditions
  const currentRequestRef = useRef<Promise<AddressSuggestion[]> | null>(null);

  // Debounced function to fetch address suggestions
  const debouncedFetchSuggestions = useCallback(
    debounce(async (query: string) => {
      if (query.trim().length < 2) {
        setCommuteSuggestions([]);
        return;
      }

      // Cancel any existing request
      if (currentRequestRef.current) {
        currentRequestRef.current = null;
      }

      setIsLoadingSuggestions(true);
      
      try {
        const requestPromise = getAddressSuggestions(query, 5);
        currentRequestRef.current = requestPromise;
        
        const suggestions = await requestPromise;
        
        // Only update if this is still the current request
        if (currentRequestRef.current === requestPromise) {
          // Additional deduplication at the component level
          const uniqueSuggestions = suggestions.filter((item: any, index: number, self: any[]) => 
            index === self.findIndex((t: any) => 
              t.display_name === item.display_name && 
              t.lat === item.lat && 
              t.lon === item.lon
            )
          );
          setCommuteSuggestions(uniqueSuggestions);
        }
      } catch {
        // Only update if this is still the current request
        if (currentRequestRef.current) {
          setCommuteSuggestions([]);
        }
      } finally {
        // Only update loading state if this is still the current request
        if (currentRequestRef.current) {
          setIsLoadingSuggestions(false);
          currentRequestRef.current = null;
        }
      }
    }, 300), // Reduced debounce time for better responsiveness
    []
  );

  // Handlers from original Home.tsx
  const handleCommuteInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    
    setCommuteAddressRent(input);
    setCommuteAddressTouched(true);
    setAutocompleteActiveIndex(-1);
    
    // Fetch suggestions as user types
    debouncedFetchSuggestions(input);
    
    // If the input is not empty, try to geocode it
    if (input.trim().length > 0) {
      try {
        const { geocodeAddress } = await import('../../../lib/utils');
        const coords = await geocodeAddress(input);
        if (coords) {
          setCommuteCoords(coords);
        }
      } catch {
      }
    } else {
      setCommuteCoords(null);
    }
  };

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    setCommuteAddressRent(suggestion.display_name);
    setCommuteSuggestions([]);
    setAutocompleteActiveIndex(-1);
    
    // Set coordinates for the selected address
    const coords = {
      lat: parseFloat(suggestion.lat),
      lng: parseFloat(suggestion.lon)
    };
    setCommuteCoords(coords);
  };

  const handleCommuteInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (commuteSuggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setAutocompleteActiveIndex((prev) => Math.min(prev + 1, commuteSuggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAutocompleteActiveIndex((prev) => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (autocompleteActiveIndex >= 0 && autocompleteActiveIndex < commuteSuggestions.length) {
        handleSuggestionSelect(commuteSuggestions[autocompleteActiveIndex]);
      }
    } else if (e.key === 'Escape') {
      setCommuteSuggestions([]);
      setAutocompleteActiveIndex(-1);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        autocompleteRef.current &&
        !autocompleteRef.current.contains(event.target as Node) &&
        autocompleteDropdownRef.current &&
        !autocompleteDropdownRef.current.contains(event.target as Node)
      ) {
        setCommuteSuggestions([]);
        setAutocompleteActiveIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUseCurrentAddress = async () => {
    
    if (!navigator.geolocation) {
      toast({
        title: 'Geolocation Not Supported',
        description: 'Geolocation is not supported by your browser. Please enter your address manually.',
        variant: 'destructive'
      });
      return;
    }

    setIsGettingLocation(true);
    
    const geolocationOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds
      maximumAge: 300000 // 5 minutes
    };
    
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          const result = await reverseGeocode(latitude, longitude);
          
          if (result && (result.fullAddress || result.city || result.state)) {
            // Use the most specific address available
            let address = '';
            
            if (result.fullAddress && result.fullAddress.trim()) {
              // Use the full detailed address
              address = result.fullAddress;
            } else if (result.streetAddress && result.city && result.state) {
              // Build address from components
              address = `${result.streetAddress}, ${result.city}, ${result.state}`;
              if (result.postalCode) address += ` ${result.postalCode}`;
            } else if (result.city && result.state) {
              // Fallback to city, state
              address = `${result.city}, ${result.state}`;
              if (result.postalCode) address += ` ${result.postalCode}`;
            } else if (result.displayName) {
              // Use display name as fallback
              address = result.displayName;
            } else {
              // Last resort: use coordinates
              address = `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
            }
            
            setCommuteAddressRent(address);
            setCommuteSuggestions([]);
            setAutocompleteActiveIndex(-1);
            
            // Also set coordinates for drive time calculation
            const coords = { lat: latitude, lng: longitude };
            setCommuteCoords(coords);
            
          } else {
            // Fallback: use coordinates as address
            const address = `Location (${latitude.toFixed(4)}, ${longitude.toFixed(4)})`;
            setCommuteAddressRent(address);
            setCommuteSuggestions([]);
            setAutocompleteActiveIndex(-1);
            
            const coords = { lat: latitude, lng: longitude };
            setCommuteCoords(coords);
          }
        } catch (error) {
          toast({
            title: 'Location Error',
            description: 'Unable to get address from your location. Please enter your address manually.',
            variant: 'destructive'
          });
        } finally {
          setIsGettingLocation(false);
        }
      }, 
      (error) => {
        
        let errorMessage = 'Unable to retrieve your location. ';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += 'Please allow location access and try again.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage += 'Location request timed out. Please try again.';
            break;
          default:
            errorMessage += 'Please enter your address manually.';
            break;
        }
        
        toast({
          title: 'Location Error',
          description: errorMessage,
          variant: 'destructive'
        });
        setIsGettingLocation(false);
      },
      geolocationOptions
    );
  };

  // UI
  return (
    <div className="mb-4">
      <span className="text-lg font-medium text-foreground block mb-2">Commute</span>
      <div className="flex items-center mb-2">
        <div className="flex-1 flex items-center bg-muted rounded-full px-4 py-3 relative">
          <MapPin className="h-5 w-5 text-muted-foreground mr-2" />
          {true ? (
            <>
              <input
                type="text"
                placeholder="Enter your commute address"
                className="bg-transparent border-0 outline-none flex-1 text-base text-muted-foreground placeholder:text-muted-foreground"
                value={commuteAddressRent}
                onChange={handleCommuteInputChange}
                onFocus={() => setCommuteAddressTouched(true)}
                onKeyDown={handleCommuteInputKeyDown}
                ref={autocompleteRef}
              />
              {commuteSuggestions.length > 0 && (
                <div
                  ref={autocompleteDropdownRef}
                  className="absolute left-0 right-0 top-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-60 overflow-y-auto mt-1"
                >
                  <button
                    type="button"
                    className="w-full flex items-center gap-2 rounded-t-xl bg-[#e6f0fe] text-[#1967d2] px-4 py-2 cursor-pointer hover:bg-blue-100 mb-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={handleUseCurrentAddress}
                    disabled={isGettingLocation}
                  >
                    {isGettingLocation ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-[#1967d2] border-t-transparent"></div>
                        Getting location...
                      </>
                    ) : (
                      <>
                        <MapPin className="w-5 h-5" />
                        Use current location
                      </>
                    )}
                  </button>
                  {commuteSuggestions.map((suggestion, idx) => (
                    <div
                      key={`${suggestion.place_id}-${idx}-${suggestion.lat}-${suggestion.lon}`}
                      className={`px-4 py-2 cursor-pointer hover:bg-blue-50 text-sm ${idx === autocompleteActiveIndex ? 'bg-blue-100' : ''}`}
                      onMouseDown={() => handleSuggestionSelect(suggestion)}
                    >
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-gray-400" />
                        <span className="truncate">{suggestion.display_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {isLoadingSuggestions && (
                <div className="absolute left-0 right-0 top-full bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1">
                  <div className="px-4 py-2 text-sm text-gray-500 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    Searching...
                  </div>
                </div>
              )}
            </>
          ) : (
            <input
              type="text"
              placeholder="Enter address..."
              className="bg-transparent border-0 outline-none flex-1 text-base text-muted-foreground placeholder:text-muted-foreground"
              value={commuteAddressRent}
              disabled
            />
          )}
        </div>
      </div>
      
      {/* Standalone "Use current location" button */}
      <div className="mb-3">
        <button
          type="button"
          onClick={handleUseCurrentAddress}
          disabled={isGettingLocation}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-200 ${
            isGettingLocation 
              ? 'bg-muted text-muted-foreground cursor-not-allowed' 
              : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
          }`}
        >
          {isGettingLocation ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-2 border-current border-t-transparent"></div>
              Getting location...
            </>
          ) : (
            <>
              <MapPin className="h-3 w-3" />
              Use current location
            </>
          )}
        </button>
      </div>
      
      <div className="text-xs text-muted-foreground mb-2">Select maximum drive time</div>
      <div className="flex gap-2 flex-wrap pb-1">
        {[null, 10, 20, 30, 40, 50, 60].map((val) => (
          <button
            key={val === null ? 'any' : val}
            className={`rounded-full border border-border text-foreground font-semibold px-4 py-2 text-base bg-background ${commuteDriveTimeRent === val ? 'bg-primary text-primary-foreground' : ''}`}
            onClick={() => setCommuteDriveTimeRent(val)}
            type="button"
            disabled={!commuteAddressRent || isCalculatingDriveTimes}
            style={!commuteAddressRent || isCalculatingDriveTimes ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            {val === null ? 'Any' : `${val} min`}
          </button>
        ))}
      </div>
      
      {/* Drive time calculation loading indicator */}
      {isCalculatingDriveTimes && (
        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
          <span>Calculating drive times...</span>
        </div>
      )}
    </div>
  );
};

export default CommuteFilter; 